import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import { border, celdaTexto, celdaNumero, celdaFecha } from "../../../../Components/ExcelEstilos";
import { COLORES_ESTADO } from "../MapaHotel/Components/HabitacionCanvas";

/**
 * Excel del reporte de Ocupación a Futuro: la misma matriz habitación × día que se ve en pantalla.
 *
 * Los colores se derivan de COLORES_ESTADO para que el archivo y la pantalla no puedan divergir. Los
 * de la pantalla llevan alfa ("#ef44441f") y Excel solo entiende RRGGBB opaco, así que se toma el
 * color de texto de cada estado y se usa un relleno claro fijo por estado: lo que importa es que se
 * distingan de un vistazo, no reproducir el tono exacto.
 *
 * Dos reglas de xlsx-js-style que ya nos costaron un bug en otros reportes:
 *   · Toda celda numérica necesita `t: "n"` explícito. Sin él la librería la escribe como texto y
 *     Excel muestra el triángulo verde de "número guardado como texto" y no la puede sumar.
 *   · `!freeze` y `!panes` NO hacen nada (son de SheetJS Pro), así que no se intenta congelar la
 *     columna de habitaciones. Tampoco se usa `!autofilter`: se comería el renglón de porcentajes.
 */

const RELLENOS = {
  ocupada: "FBD5D5",
  programada: "E3DAFB",
  libre: "DFF3E4",
  sinTarifa: "ECEDEF",
};

const sinNumeral = (color) => String(color || "").replace("#", "").toUpperCase();

const estiloCelda = (estado) => ({
  font: { sz: 9, bold: estado === "ocupada" || estado === "programada", color: { rgb: sinNumeral(COLORES_ESTADO[estado].texto) } },
  fill: { fgColor: { rgb: RELLENOS[estado] } },
  alignment: { horizontal: "center", vertical: "center" },
  border,
});

const estiloEncabezado = {
  font: { bold: true, sz: 9, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "00BFD6" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};

const estiloTitulo = {
  font: { bold: true, sz: 12, color: { rgb: "005661" } },
  alignment: { horizontal: "left", vertical: "center" },
};

const estiloHabitacion = {
  font: { sz: 10 },
  alignment: { horizontal: "left", vertical: "center" },
  border,
};

const estiloPorcentaje = {
  font: { bold: true, sz: 9 },
  fill: { fgColor: { rgb: "FFF2CC" } },
  alignment: { horizontal: "center", vertical: "center" },
  numFmt: "0",
  border,
};

const estadoDeCelda = (celda, habitacion) => {
  if (celda && celda.tipo === "ocupada") return "ocupada";
  if (celda && celda.tipo === "programada") return "programada";
  if (!habitacion.insumoId) return "sinTarifa";
  return "libre";
};

const descargarExcelOcupacionFuturo = ({ datos, nombreHotel, nombreNivel, incluirProgramadas }) => {
  const dias = datos.dias || [];
  const habitaciones = datos.habitaciones || [];
  const resumenPorDia = datos.resumenPorDia || [];
  const periodos = datos.periodos || [];
  const resumenPorPeriodo = datos.resumenPorPeriodo || [];

  // El backend agrega por mes cuando el rango pasa de tres meses: la matriz día por día de un año no
  // cabe ni en la pantalla ni en una hoja legible. El archivo sigue la misma decisión.
  const esMensual = datos.granularidad === "mes";
  const columnas = esMensual ? periodos : dias;
  const resumen = esMensual ? resumenPorPeriodo : resumenPorDia;

  const filas = [];

  filas.push([
    { v: `Ocupacion a futuro - ${nombreHotel}${nombreNivel ? " / " + nombreNivel : ""}`, s: estiloTitulo },
  ]);
  filas.push([
    {
      v:
        `Del ${moment(datos.desde).format("DD/MM/YYYY")} al ${moment(datos.hasta).format("DD/MM/YYYY")}` +
        (incluirProgramadas ? " · incluye ventas programadas" : " · solo ventas capturadas"),
      s: { font: { sz: 9, italic: true, color: { rgb: "6B7280" } } },
    },
  ]);
  filas.push([]);

  // Encabezado de columnas: un día, o un mes cuando el rango es largo.
  filas.push([
    { v: "Habitacion", s: estiloEncabezado },
    ...columnas.map((valorColumna) => ({
      v: esMensual
        ? moment(valorColumna.clave + "-01", "YYYY-MM-DD").format("MMM YYYY")
        : moment(valorColumna).format("ddd DD/MM"),
      s: estiloEncabezado,
    })),
  ]);

  // Renglón de porcentaje. Va como número con numFmt para que se pueda graficar y promediar.
  filas.push([
    { v: "% de ocupacion", s: { ...estiloEncabezado, fill: { fgColor: { rgb: "F8CBAD" } }, font: { bold: true, sz: 9, color: { rgb: "7C2D12" } } } },
    ...resumen.map((valorResumen) =>
      celdaNumero(
        incluirProgramadas
          ? valorResumen.porcentajeOcupacionConProgramadas
          : valorResumen.porcentajeOcupacion,
        estiloPorcentaje
      )
    ),
  ]);

  // Las estancias que aparecen en la matriz, cada una una sola vez. Se juntan mientras se recorren
  // las celdas para poder listarlas abajo con nombre y fechas: en una columna de 7 caracteres no cabe
  // un nombre, así que la matriz lleva solo el marcador y el detalle va aparte.
  const estancias = [];
  const vistas = new Set();

  const etiquetaHabitacion = (valorHabitacion) =>
    `${valorHabitacion.numero}` +
    (valorHabitacion.nombre ? ` - ${valorHabitacion.nombre}` : "") +
    (valorHabitacion.nivelNombre ? ` (${valorHabitacion.nivelNombre})` : "");

  habitaciones.forEach((valorHabitacion) => {
    const etiqueta = etiquetaHabitacion(valorHabitacion);

    if (esMensual) {
      // Una celda por mes: las noches ocupadas, teñidas por qué tan lleno quedó. El morado se reserva
      // para el mes que solo tiene proyección, que es información distinta de "mes flojo".
      filas.push([
        celdaTexto(etiqueta, estiloHabitacion),
        ...(valorHabitacion.celdasPeriodo || []).map((valorCelda) => {
          if (!valorCelda || valorCelda.noches === 0) {
            return celdaNumero(0, estiloCelda(valorHabitacion.insumoId ? "libre" : "sinTarifa"));
          }
          const proporcion =
            valorCelda.diasDisponibles > 0 ? valorCelda.noches / valorCelda.diasDisponibles : 0;
          const estado =
            valorCelda.nochesVendidas === 0
              ? "programada"
              : proporcion >= 0.9
              ? "ocupada"
              : "libre";
          return celdaNumero(valorCelda.noches, estiloCelda(estado));
        }),
      ]);
      return;
    }

    filas.push([
      celdaTexto(etiqueta, estiloHabitacion),
      ...valorHabitacion.celdas.map((valorCelda) => {
        const estado = estadoDeCelda(valorCelda, valorHabitacion);

        if (valorCelda) {
          const llave = `${valorHabitacion._id}|${valorCelda.tipo}|${valorCelda.folio}|${valorCelda.fechaLlegada}`;
          if (!vistas.has(llave)) {
            vistas.add(llave);
            estancias.push({ habitacion: etiqueta, ...valorCelda });
          }
        }

        // Mismo marcador que la pantalla: V = venta capturada, P = venta programada.
        const texto = valorCelda ? (valorCelda.tipo === "programada" ? "P" : "V") : "";
        return { v: texto, s: estiloCelda(estado) };
      }),
    ]);
  });

  // En modo mensual la pantalla no recibe las celdas por día, así que la lista de estancias la arma
  // el backend y llega ya hecha.
  if (esMensual) {
    (datos.estancias || []).forEach((valorEstancia) => {
      estancias.push({
        ...valorEstancia,
        habitacion:
          `${valorEstancia.habitacionNumero}` +
          (valorEstancia.habitacionNombre ? ` - ${valorEstancia.habitacionNombre}` : "") +
          (valorEstancia.nivelNombre ? ` (${valorEstancia.nivelNombre})` : ""),
      });
    });
  }

  filas.push([]);

  // Desglose numérico, para quien quiera la cifra sin leer la matriz. En mensual se cuentan
  // noches-habitación del periodo; en diario, cuartos de ese día.
  filas.push([
    { v: esMensual ? "Mes" : "Dia", s: estiloEncabezado },
    { v: esMensual ? "Noches disponibles" : "Habitaciones", s: estiloEncabezado },
    { v: "Vendidas", s: estiloEncabezado },
    { v: "Programadas", s: estiloEncabezado },
    { v: "Libres", s: estiloEncabezado },
    { v: "% ocupacion", s: estiloEncabezado },
  ]);
  resumen.forEach((valorResumen) => {
    filas.push([
      celdaTexto(
        esMensual
          ? moment(valorResumen.clave + "-01", "YYYY-MM-DD").format("MMMM YYYY")
          : moment(valorResumen.fecha).format("DD/MM/YYYY"),
        estiloHabitacion
      ),
      celdaNumero(
        esMensual ? valorResumen.nochesDisponibles : valorResumen.total,
        estiloCelda("libre")
      ),
      celdaNumero(
        esMensual ? valorResumen.nochesVendidas : valorResumen.ocupadas,
        estiloCelda("ocupada")
      ),
      celdaNumero(
        esMensual ? valorResumen.nochesProgramadas : valorResumen.programadas,
        estiloCelda("programada")
      ),
      celdaNumero(esMensual ? valorResumen.nochesLibres : valorResumen.libres, estiloCelda("libre")),
      celdaNumero(
        incluirProgramadas
          ? valorResumen.porcentajeOcupacionConProgramadas
          : valorResumen.porcentajeOcupacion,
        estiloPorcentaje
      ),
    ]);
  });

  filas.push([]);

  // Detalle de estancias: lo que la matriz no puede decir por falta de ancho.
  filas.push([
    { v: "Habitacion", s: estiloEncabezado },
    { v: "Origen", s: estiloEncabezado },
    { v: "Folio", s: estiloEncabezado },
    { v: "Cliente", s: estiloEncabezado },
    { v: "Llegada", s: estiloEncabezado },
    { v: "Salida", s: estiloEncabezado },
  ]);
  estancias
    .sort(
      (a, b) =>
        String(a.habitacion).localeCompare(String(b.habitacion), "es", { numeric: true }) ||
        new Date(a.fechaLlegada) - new Date(b.fechaLlegada)
    )
    .forEach((valorEstancia) => {
      const estado = valorEstancia.tipo === "programada" ? "programada" : "ocupada";
      filas.push([
        celdaTexto(valorEstancia.habitacion, estiloHabitacion),
        { v: valorEstancia.tipo === "programada" ? "Venta programada" : "Venta", s: estiloCelda(estado) },
        celdaTexto(valorEstancia.folio || "", estiloHabitacion),
        celdaTexto(valorEstancia.clienteNombre || "", estiloHabitacion),
        celdaFecha(valorEstancia.fechaLlegada),
        celdaFecha(valorEstancia.fechaSalida),
      ]);
    });

  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  // Las columnas de día son angostas a propósito: lo que llevan es un estado, no un importe, y con
  // 90 días una hoja de columnas anchas se vuelve imposible de leer. Las de mes sí necesitan caber el
  // nombre. El desglose numérico va abajo en los dos casos.
  hoja["!cols"] = [{ wch: 30 }, ...columnas.map(() => ({ wch: esMensual ? 11 : 7 }))];

  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Ocupacion a futuro");
  XLSXStyle.writeFile(
    libro,
    `Ocupacion a futuro ${moment(datos.desde).format("DD-MM-YYYY")} a ${moment(datos.hasta).format("DD-MM-YYYY")}.xlsx`
  );
};

export default descargarExcelOcupacionFuturo;
